<template>
  <Spinner :loading="orderdetails.loading" />
  <ContactRep @openCalendlyModal="openCalendlyModal" />

  <ModalFull :is-visible="showCalendly" :reset-container="true" container-class="w-full max-w-5xl" :isOverApp="true"
    :isBlurOverlay="true">
    <div class="w-full rounded-lg bg-white shadow-box">
      <div class="flex justify-between px-5 py-3 w-full">
        <div class="flex items-start justify-start space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
            stroke="currentColor" class="h-4 w-4 flex-shrink-0">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
          </svg>
          <h3 class="text-lg text-theme-secondary font-semibold">Schedule a meeting</h3>
        </div>
        <div class="close cursor-pointer hover:bg-sky-700" @click="showCalendly = false">
          <span>X</span>
        </div>
      </div>
      <div class="calendly-inline-widget" id="calendly-inline-widget"
        style="position: relative; min-width: 320px; height: 90vh;" data-auto-load="false"></div>
    </div>
  </ModalFull>

  <div v-if="!orderdetails.loading"
    class="w-auto min-w-full pb-10 -m-3 sm:mx-0 flex flex-col-reverse lg:flex-row items-start gap-6">
    <div class="flex-grow space-y-6 w-full lg:w-auto overflow-hidden">
      <OrderInfoTopBar :is-on-hold="isOnHold" :status-label="statusLabel" :subtitle="statusSubtitle"
        :invoices="orderInvoices" :view-order-to="viewOrderTo" :order-id="orderData.id"
        :order-number="displayOrderNumber" :reference-number="displayReferenceNumber" :payment-label="paymentLabel"
        :shipping-name="shippingName" :shipping-address="shippingAddress" :billing-name="billingName"
        :billing-address="billingAddress" :rep-name="repName" :rep-email="repEmail" :rep-phone="repPhone"
        :shipping-help-text="shippingHelpText" :placed-at="placedAt" class="tq-checkout-block" />

      <OrderLifecycleBar :order="orderData" :shipments="shipmentList" />

      <PaymentInstructionsBlock v-if="!isPaid" :selected-method="selectedPaymentMethod" :zelle-email="zelleEmail"
        :zelle-reference="zelleReference" :total-amount="orderTotal" :selected-invoices-count="selectedInvoicesCount"
        :copied-key="copiedKey" :copy-to-clipboard="copyToClipboard" :currency-format="currencyFormat"
        :wire-details="wireDetails" :check-pay-to="checkPayTo" :check-mail-to="checkMailTo"
        :invoice-email="invoiceEmail" />

      <AccountStatusBlock v-if="orderData.company?.self_pay == 1" :state-key="accountStateKey" :is-paid="isPaid" :past-due-invoices="pastDueInvoices"
        :due-soon-invoices="dueSoonInvoices" :past-due-total="pastDueTotal" :open-balance-total="openBalanceTotal"
        :invoices-page-to="invoicesPageTo" :currency-format="currencyFormat" />
    </div>

    <div class="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-5 space-y-3.5">
      <div class="thankyou-card thankyou-card-pad">
        <OrderInformationCard :rows="thankyouOrderInfoRows" :copied-key="copiedKey" :on-copy="handleOrderInfoCopy" />
      </div>

      <div class="thankyou-card overflow-hidden">
        <div class="thankyou-side-pad py-3.5 border-b thankyou-divider flex items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-theme-secondary">Order Summary</h3>
          <span class="text-xs text-theme-body">{{ totalSkus }} SKUs · {{ totalUnits }} units</span>
        </div>

        <div class="thankyou-side-pad py-2.5">
          <div class="thankyou-summary-head">
            <p>Product</p>
            <p class="text-right">Qty</p>
            <p class="text-right">Total</p>
          </div>

          <div class="thankyou-summary-list" :class="{ 'thankyou-summary-list--expanded': showAllSummaryItems }">
            <div v-for="(item, idx) in visibleSummaryItems" :key="item.id || idx" class="thankyou-summary-row">
              <div class="flex items-center gap-2.5 min-w-0">
                <img :src="item.image_url || '/images/no-image.png'" :alt="item.productName"
                  class="w-8 h-8 object-cover thankyou-item-image" />
                <div class="min-w-0">
                  <p class="thankyou-item-name">{{ item.productName }}</p>
                  <p class="thankyou-item-sub">{{ item.optionLabel || 'Default' }}</p>
                </div>
              </div>
              <div class="thankyou-item-qty">{{ item.qty }}</div>
              <div class="thankyou-item-total">${{ commaFormat((Number(item.singlePrice || 0) * Number(item.qty ||
                0)).toFixed(2)) }}</div>
            </div>
          </div>

          <button v-if="hasMoreSummaryItems" type="button" class="thankyou-see-all"
            @click="showAllSummaryItems = !showAllSummaryItems">
            <span>{{ showAllSummaryItems ? 'See fewer items' : `See all ${sortedLineItems.length} items` }}</span>
            <span aria-hidden="true">↗</span>
          </button>
        </div>

        <div class="thankyou-side-pad pt-2.5 pb-4 border-t thankyou-divider text-sm">
          <div class="thankyou-total-row">
            <span>Subtotal</span>
            <span>${{ commaFormat(lineItemsTotal.toFixed(2)) }}</span>
          </div>
          <div class="thankyou-total-row">
            <span>Shipping</span>
            <span>${{ commaFormat(orderShipping.toFixed(2)) }}</span>
          </div>
          <div class="thankyou-total-row" v-if="orderTaxes > 0">
            <span>Taxes</span>
            <span>${{ commaFormat(orderTaxes.toFixed(2)) }}</span>
          </div>
          <div v-for="discount in orderDiscounts" :key="discount.id" class="thankyou-total-row">
            <span>{{ discount.label }}</span>
            <span>-${{ commaFormat(Math.abs(Number(discount.price || 0)).toFixed(2)) }}</span>
          </div>

          <div class="h-px thankyou-divider-line my-2"></div>

          <div class="thankyou-grand-total">
            <span>Total</span>
            <span>${{ commaFormat(orderTotal.toFixed(2)) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import OrderInfoTopBar from "./modules/OrderInfoTopBar.vue";
import OrderLifecycleBar from "./modules/OrderLifecycleBar.vue";
import PaymentInstructionsBlock from "./modules/PaymentInstructionsBlock.vue";
import AccountStatusBlock from "./modules/AccountStatusBlock.vue";
import OrderInformationCard from "./modules/OrderInformationCard.vue";
import ContactRep from "@/views/layouts/modules/ContactRep.vue";
import Account from "@/modules/Account";
import Mymixin from "@/modules/mixins";

const route = useRoute();
const axios = inject("axios");

const showCalendly = ref(false);
const openCalendlyModal = (url) => {
  showCalendly.value = true;
  setTimeout(() => {
    window.Calendly?.initInlineWidget({
      url,
      parentElement: document.getElementById("calendly-inline-widget"),
    });
  }, 300);
};

const currencyFormat = Mymixin.methods.currencyFormat;
const commaFormat = Mymixin.methods.commaFormat;

const { orderdetails, getOrderdetails } = Account("thankyou");

const copiedKey = ref("");
const invoicesData = ref({
  allInvoices: [],
  dueInvoices: [],
  openInvoicesNotDue: [],
});

const wireDetails = ref({
  bankName: "",
  bankAddress: "",
  routing: "",
  account: "",
  accountName: "",
  accountType: "",
  swiftCode: "",
  businessAddress: "",
});
const zelleEmail = ref("");
const invoiceEmail = ref("");
const checkPayTo = ref("");
const checkMailTo = ref("");

const orderData = computed(() => orderdetails.data?.data || {});
const shipmentList = computed(() => {
  const raw = orderdetails.data?.shipments;
  return Array.isArray(raw) ? raw : [];
});
const hasOrderData = computed(() => Object.keys(orderData.value || {}).length > 0);
const orderInvoices = computed(() => {
  const apiInvoices = orderData.value?.invoice || [];
  if (apiInvoices.length) return apiInvoices;
  return [{ id: route.params.id || "mock-invoice" }];
});
const orderLineItems = computed(() => orderData.value?.order_line_items || []);
const orderDiscounts = computed(() => orderData.value?.discounts || []);

const sortedLineItems = computed(() => {
  return [...orderLineItems.value].sort((a, b) =>
    (a.productName || "").localeCompare(b.productName || ""),
  );
});

const showAllSummaryItems = ref(false);

const visibleSummaryItems = computed(() => {
  if (showAllSummaryItems.value || sortedLineItems.value.length <= 6) {
    return sortedLineItems.value;
  }
  return sortedLineItems.value.slice(0, 6);
});

const hasMoreSummaryItems = computed(() => sortedLineItems.value.length > 6);

const thankyouOrderInfoRows = computed(() => [
  {
    label: "Invoice #",
    value: displayInvoiceNumber.value,
    copyKey: "invoice-number",
    mono: true,
  },
  {
    label: "Reference",
    value: displayReferenceNumber.value,
    copyKey: "reference-number",
    mono: true,
  },
]);

const totalSkus = computed(() => sortedLineItems.value.length);

const totalUnits = computed(() => {
  return sortedLineItems.value.reduce((sum, item) => sum + Number(item.qty || 0), 0);
});

const lineItemsTotal = computed(() => {
  return orderLineItems.value.reduce(
    (acc, item) => acc + Number(item.qty || 0) * Number(item.singlePrice || 0),
    0,
  );
});

const orderShipping = computed(() => Number(orderData.value?.shippingTotal || 0));
const orderTaxes = computed(() => Number(orderData.value?.taxes || 0));
const orderTotal = computed(() => {
  const value = Number(orderData.value?.orderTotal || 0);
  if (value) return value;
  return lineItemsTotal.value + orderShipping.value + orderTaxes.value;
});

const displayOrderNumber = computed(() => orderData.value?.order_number || "");

const displayReferenceNumber = computed(() => orderData.value?.reference_number || "");

const displayInvoiceNumber = computed(() => {
  return (
    orderData.value?.invoice_number ||
    orderData.value?.invoice?.[0]?.invoice_number ||
    orderInvoices.value?.[0]?.invoice_number ||
    orderInvoices.value?.[0]?.id ||
    ""
  );
});

const paymentPosture = computed(() => {
  const hasPaidCard = Array.isArray(orderData.value?.payments)
    ? orderData.value.payments.length > 0
    : false;

  if (hasPaidCard) {
    return "paid";
  }

  const paymentTerm = String(orderData.value?.payment_term || "").toLowerCase();
  if (paymentTerm.includes("zelle")) return "awaiting-zelle";
  if (
    paymentTerm.includes("wire") ||
    paymentTerm.includes("bank transfer") ||
    paymentTerm.includes("ach")
  ) {
    return "awaiting-wire";
  }
  if (paymentTerm.includes("check")) return "awaiting-check";
  if (paymentTerm.includes("net") || paymentTerm.includes("pay later")) {
    return "net30";
  }
  return "awaiting-wire";
});

const isPaid = computed(() => paymentPosture.value === "paid");

const selectedPaymentMethod = computed(() => {
  if (paymentPosture.value === "awaiting-zelle") return "zelle";
  if (paymentPosture.value === "awaiting-check") return "check";
  return "wire";
});

const paymentLabel = computed(() => {
  if (paymentPosture.value === "paid") return "Paid by card";
  if (paymentPosture.value === "awaiting-zelle") return "Awaiting Zelle";
  if (paymentPosture.value === "awaiting-check") return "Awaiting Check";
  if (paymentPosture.value === "net30") return "Net 30";
  return "Awaiting Wire";
});

const invoicePayload = computed(() => invoicesData.value || {});

const allUnpaidInvoices = computed(() => {
  const all = [
    ...(invoicePayload.value?.allInvoices || []),
    ...(invoicePayload.value?.dueInvoices || []),
    ...(invoicePayload.value?.openInvoicesNotDue || []),
  ];

  const seen = new Set();
  return all.filter((invoice) => {
    if (!invoice || seen.has(invoice.id) || invoice.paid) return false;
    seen.add(invoice.id);
    return true;
  });
});

const pastDueInvoices = computed(() => {
  return allUnpaidInvoices.value.filter((invoice) =>
    moment(invoice.due_at).isBefore(moment(), "day"),
  );
});

const dueSoonInvoices = computed(() => {
  return allUnpaidInvoices.value.filter((invoice) => {
    const dueDate = moment(invoice.due_at);
    return (
      dueDate.isSameOrAfter(moment(), "day") &&
      dueDate.isSameOrBefore(moment().add(14, "days"), "day")
    );
  });
});

const openBalanceTotal = computed(() => {
  return allUnpaidInvoices.value.reduce(
    (acc, invoice) => acc + Number(invoice.payable_amount || 0),
    0,
  );
});

const pastDueTotal = computed(() => {
  return pastDueInvoices.value.reduce(
    (acc, invoice) => acc + Number(invoice.payable_amount || 0),
    0,
  );
});

const accountStateKey = computed(() => {
  if (pastDueInvoices.value.length > 0) return "past-due";
  if (allUnpaidInvoices.value.length > 0) return "open-balance";
  return "healthy";
});

const holdReason = computed(() => {
  const paymentHold = orderData.value?.order_holds?.find(
    (hold) => hold.hold_status === "Hold - Payment",
  );
  return paymentHold?.hold_status || "";
});

const isOnHold = computed(() => {
  return !isPaid.value && holdReason.value === "terms";
});

const statusLabel = computed(() => {
  if (isPaid.value) return "Paid";
  if (isOnHold.value) return "On Hold";
  return "Awaiting Payment";
});

const statusSubtitle = computed(() => {
  if (isPaid.value) {
    return "Payment has been confirmed and your order is moving to fulfillment.";
  }
  if (isOnHold.value) {
    return "We received your order. Please clear past-due invoices to release it for fulfillment.";
  }
  return "Your order is placed. Complete payment using one of the methods below.";
});

const selectedInvoicesCount = computed(() => {
  if (orderInvoices.value.length) return orderInvoices.value.length;
  return allUnpaidInvoices.value.length || 1;
});

const zelleReference = computed(() => {
  const base =
    displayReferenceNumber.value ||
    displayOrderNumber.value ||
    `ORDER-${route.params.id || ""}`;
  return String(base).slice(0, 40);
});

const placedAt = computed(() => {
  const raw = orderData.value?.created_at || null;
  return raw ? moment(raw, ["MM/DD/YYYY hh:mm A", moment.ISO_8601]).format("MMM D, YYYY h:mm A") : "";
});

const stringifyAddress = (address) => {
  if (!address) return "";
  const company = address.nickname || address.company_name || "";
  const line1 = address.address1 || "";
  const line2 = address.address2 || "";
  const cityStateZip = [address.city, address.state, address.zip_code]
    .filter(Boolean)
    .join(" ");

  return [company, line1, line2, cityStateZip].filter(Boolean).join("\n") || "";
};

const shippingName = computed(() => {
  const addr = orderData.value?.shipping_address;
  return addr?.nickname || addr?.company_name || "";
});

const shippingAddress = computed(() => stringifyAddress(orderData.value?.shipping_address) || "");

const billingName = computed(() => orderData.value?.billing_address?.company_name || "");

const billingAddress = computed(() => stringifyAddress(orderData.value?.billing_address) || "");

const repName = computed(() => orderData.value?.sales_rep?.name || "");
const repEmail = computed(() => orderData.value?.sales_rep?.email || "");
const repPhone = computed(() => orderData.value?.sales_rep?.phone || "");

const shippingHelpText = computed(() => "Wrong address? Contact us before it ships");

const viewOrderTo = computed(() => {
  if (route.params?.id) {
    return `/order-details/${route.params.id}`;
  }
  return "/account/orders";
});

const invoicesPageTo = computed(() => {
  const companyId = orderData.value?.encrypted_company_id;

  if (companyId) {
    return `/invoice-payments/${companyId}`;
  }

  return "/account/orders";
});

const copyToClipboard = (text, key) => {
  navigator.clipboard.writeText(text || "").catch(() => { });
  copiedKey.value = key;
  setTimeout(() => {
    copiedKey.value = "";
  }, 2000);
};

const handleOrderInfoCopy = (copyKey, value) => {
  copyToClipboard(value, copyKey);
};

const fetchBankDetails = async () => {
  try {
    const response = await axios.authApi.get("/account/bank-details");
    const details = response.data?.data || {};

    wireDetails.value = {
      bankName: details.bank_name || "",
      bankAddress: details.bank_address || "",
      routing: details.routing_number || "",
      account: details.account_number || "",
      accountName: details.account_name || "",
      accountType: details.account_type || "",
      swiftCode: details.swift_code || "",
      businessAddress: details.business_address || "",
    };
    zelleEmail.value = details.zelle_email || "";
    invoiceEmail.value = details.invoice_email || "";
    checkPayTo.value = details.check_pay_to || "";
    checkMailTo.value = details.check_mail_to || "";
  } catch (error) {
    console.error("Failed to load bank details", error);
  }
};

const fetchInvoicePayments = async (companyId) => {
  if (!companyId) return;

  try {
    const response = await axios.api.get(
      `invoice-payments/${companyId}/${route.params.invoice_number || ""}`,
    );
    const payload = response.data?.data || {
      allInvoices: [],
      dueInvoices: [],
      openInvoicesNotDue: [],
    };
    invoicesData.value = payload;
  } catch (error) {
    console.error("Failed to load invoice payment status", error);
  }
};

watch(
  () => orderData.value?.encrypted_company_id,
  (companyId) => {
    if (companyId && orderData.value?.company?.self_pay == 1) {
      fetchInvoicePayments(companyId);
    }
  },
  { immediate: true },
);

onMounted(async () => {
  fetchBankDetails();

  await getOrderdetails();

  const fallbackCompanyId =
    route.params.encrypted_company_id || localStorage.getItem("encrypted_company_id");
  if (
    fallbackCompanyId &&
    !orderData.value?.encrypted_company_id &&
    orderData.value?.company?.self_pay == 1
  ) {
    fetchInvoicePayments(fallbackCompanyId);
  }
});
</script>

<style scoped>
.thankyou-card {
  background: #fff;
  border: 1px solid rgba(230, 232, 238, 0.7);
  border-radius: 14px;
}

.thankyou-card-pad {
  padding: 16px;
}

.thankyou-side-pad {
  padding-left: 16px;
  padding-right: 16px;
}

.thankyou-item-image {
  border-radius: 7px;
  background: #f5f1e8;
}

@media (min-width: 640px) {
  .thankyou-card-pad {
    padding: 18px;
  }

  .thankyou-side-pad {
    padding-left: 18px;
    padding-right: 18px;
  }
}

.thankyou-divider {
  border-color: rgba(230, 232, 238, 0.55);
}

.thankyou-divider-line {
  background: rgba(230, 232, 238, 0.55);
}

.thankyou-label {
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 600;
  color: #8c95a6;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.thankyou-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12.5px;
}

.thankyou-info-row>span {
  color: #6d7688;
}

.thankyou-info-value {
  background: transparent;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #242c3a;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.02em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.thankyou-info-value svg {
  color: #8a93a5;
}

.thankyou-summary-head {
  display: grid;
  grid-template-columns: 1fr 30px 56px;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f3efe8;
}

.thankyou-summary-head p {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: #8c95a6;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.thankyou-summary-list {
  display: flex;
  flex-direction: column;
}

.thankyou-summary-list--expanded {
  max-height: 280px;
  overflow-y: auto;
  padding-right: 6px;
}

.thankyou-summary-list--expanded::-webkit-scrollbar {
  width: 6px;
}

.thankyou-summary-list--expanded::-webkit-scrollbar-thumb {
  background: #d5dbe6;
  border-radius: 999px;
}

.thankyou-summary-row {
  display: grid;
  grid-template-columns: 1fr 30px 56px;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3efe8;
}

.thankyou-summary-row:last-child {
  border-bottom: none;
}

.thankyou-item-name {
  margin: 0;
  color: #232a38;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.thankyou-item-sub {
  margin: 0;
  color: #7a8395;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.thankyou-item-qty {
  color: #6d7688;
  font-size: 12px;
  text-align: right;
}

.thankyou-item-total {
  color: #232a38;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

.thankyou-see-all {
  width: 100%;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid rgba(226, 229, 236, 0.78);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #2b303f;
  font-size: 12px;
  font-weight: 600;
}

.thankyou-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12.5px;
}

.thankyou-total-row>span:first-child {
  color: #6d7688;
}

.thankyou-total-row>span:last-child {
  color: #242c3a;
  font-weight: 600;
}

.thankyou-grand-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 16px;
  color: #1f2633;
  font-weight: 600;
}
</style>
